/**
 * App-wide single-flight coordinator for token refresh.
 *
 * `useApiClient()` mints a separate `ApiClient` per hook call site (24+ across
 * the app), so refresh dedup state must NOT live on the ApiClient instance:
 * under a 401 storm each instance would otherwise fire its own
 * `/api/auth/refresh` call, racing the backend's refresh-token rotation
 * (each rotation invalidates the cookie used by every other in-flight
 * refresh, logging the user out).
 *
 * This module owns a single in-flight refresh promise shared by ALL
 * ApiClient instances: the first caller starts the refresh, every
 * concurrent caller awaits the same promise, and exactly one network
 * call is issued.
 *
 * @module lib/http/refreshCoordinator
 */

let inFlightRefresh: Promise<void> | null = null;

/**
 * Run `refresh` with app-wide single-flight semantics.
 *
 * If a refresh is already in flight, returns the existing promise instead of
 * starting a new one. All awaiting callers settle together: they all resolve
 * when the single refresh succeeds, or all reject when it fails.
 */
export function runSingleFlightRefresh(refresh: () => Promise<void>): Promise<void> {
  if (!inFlightRefresh) {
    inFlightRefresh = refresh().finally(() => {
      inFlightRefresh = null;
    });
  }
  return inFlightRefresh;
}

/** True while a shared refresh is in flight. */
export function isRefreshInFlight(): boolean {
  return inFlightRefresh !== null;
}

/** Test-only: drop any in-flight refresh so tests start from a clean slate. */
export function resetRefreshCoordinatorForTests(): void {
  inFlightRefresh = null;
}
